"use client";

import Link from "next/link";

import { links } from "@/data/links";
import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import { useVisitStripeChangePlan } from "@/features/stripe/services/visit-stripe-change-plan";
import { useVisitStripePayment } from "@/features/stripe/services/visit-stripe-payment";
import { useLang } from "@/hooks/use-lang";

import { Button } from "ui/core/button";

import { buttonLabels } from "../../data/button-label";

export const BasicButton = ({
  planType,
}: {
  planType: "Basic Monthly" | "Basic Yearly";
}) => {
  const { t, lang } = useLang();
  const { isAuthenticated, isFetchingUserData, profile } = useAuth();
  const currentPlan = profile?.plan;
  const isEnterprise = profile?.authProvider === "Business";
  const isSamePlan = currentPlan === planType;
  const isSamePlanButDifferentDuration =
    !isSamePlan && currentPlan?.startsWith("Basic");

  const { mutate: visitStripePayment, isLoading: isLoadingStripePayment } =
    useVisitStripePayment();
  const { mutate: visitStripeChangePlan, isLoading: isLoadingChangePlan } =
    useVisitStripeChangePlan();

  const goToPaymentPage = () => visitStripePayment({ planName: planType });

  const goToChangePlanPage = () => visitStripeChangePlan();

  if (isFetchingUserData) return <></>;

  if (isEnterprise)
    return (
      <Button disabled variant="secondary" className="w-full">
        {t.pricing.buttonText.enterprise}
      </Button>
    );

  // If user is on this plan already
  if (isSamePlan)
    return (
      <Link href={`/${lang}${links.myAnalysisSearch}`}>
        <Button
          variant="secondary"
          className="w-full"
          aria-label={buttonLabels.myAnalysisSearch}
        >
          {t.pricing.buttonText.getStarted}
        </Button>
      </Link>
    );

  // If user is same plan but different duration
  if (isSamePlanButDifferentDuration)
    return (
      <Button
        onClick={goToChangePlanPage}
        isLoading={isLoadingChangePlan}
        aria-label={buttonLabels.changePlan}
        className="w-full"
      >
        {t.pricing.buttonText.chooseThisPlan}
      </Button>
    );

  // If user is authenticated but on a different plan
  if (isAuthenticated)
    return (
      <Button
        isLoading={isLoadingStripePayment}
        onClick={goToPaymentPage}
        aria-label={buttonLabels.payment}
        className="w-full"
      >
        {t.pricing.buttonText.buyNow}
      </Button>
    );

  // For non-authenticated users
  return (
    <Link href={`/${lang}${links.authSignup}`}>
      <Button
        isLoading={isLoadingStripePayment}
        className="w-full"
        aria-label={buttonLabels.signup}
      >
        {t.pricing.buttonText.buyNow}
      </Button>
    </Link>
  );
};
